import { Box, Text } from '@chakra-ui/react';
import { iconList } from 'devicon-react-svg';
import ChartEmptyState from '@/components/ChartEmptyState';
import ChartHeader from '@/components/ChartHeader';
import { trioUITheme } from '@/styles/theme';
import { AvgTimeByTechStackData } from '@/types/api';
import { HorizontalChartBar, TechStackIcon } from './ChartAvgTimeByTechStack.styles';
import Loading from '../general/Loading';

export type ChartAvgTimeByTechStackProps = {
  title: string;
  tooltipMessage: string;
  data: AvgTimeByTechStackData[];
  isLoading: boolean;
};

const ChartAvgTimeByTechStack = ({
  title,
  tooltipMessage,
  data,
  isLoading,
}: ChartAvgTimeByTechStackProps): JSX.Element => {
  return (
    <Box
      data-testid="chart-avg-time-by-tech-stack"
      shadow="0px 4px 6px rgba(0, 0, 0, 0.07)"
      py="38px"
      px="32px"
      bg="white"
      mb="38px"
      borderRadius="16px"
    >
      <ChartHeader
        tooltipOptions={{ placement: 'bottom' }}
        title={title}
        tooltipMessage={tooltipMessage}
      />
      <Box w="100%" mt="16px">
        {data && data.length > 0 ? (
          <>
            <Box
              overflowY="auto"
              w="100%"
              h="564px"
              rowGap="8px"
              display="flex"
              flexDirection="column"
              justifyContent="start"
              alignItems="center"
              pr="8px"
              sx={{
                '&::-webkit-scrollbar': {
                  width: '6px',
                  backgroundColor: 'white',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: trioUITheme.colors['neutral-40'],
                  borderRadius: '4px',
                },
              }}
            >
              {data.map((stack, index) => (
                <Box
                  key={index.toString()}
                  bg={trioUITheme.colors['neutral-20']}
                  borderColor={trioUITheme.colors['neutral-30']}
                  borderWidth={1}
                  borderRadius="4px"
                  py="7px"
                  px="11px"
                  display="flex"
                  justifyContent="flex-start"
                  alignItems="center"
                  w="100%"
                >
                  <Box display="flex" justifyContent="flex-start" alignItems="center" minW="120px">
                    {iconList.includes(stack.tech.toLowerCase()) ? (
                      <TechStackIcon icon={stack.tech.toLowerCase()} />
                    ) : (
                      <p>{stack.tech[0]}</p>
                    )}
                    <Text ml="9px">{stack.tech}</Text>
                  </Box>
                  <Box
                    ml="20px"
                    pr="10px"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="flex-start"
                    w="100%"
                  >
                    <Box display="flex" justifyContent="flex-start" alignItems="center" w="100%">
                      <HorizontalChartBar
                        mr="10px"
                        bg={trioUITheme.colors['primary-70']}
                        h="8px"
                        w={`${stack.chart.trioPercentage}%`}
                      />
                      <Text fontSize="10px">
                        {stack.chart.trio === 0 ? 'N/A' : stack.chart.trio}
                      </Text>
                    </Box>
                    <Box display="flex" justifyContent="flex-start" alignItems="center" w="100%">
                      <HorizontalChartBar
                        mr="10px"
                        bg={trioUITheme.colors['secondary-60']}
                        h="8px"
                        w={`${stack.chart.partnersPercentage}%`}
                      />
                      <Text fontSize="10px">
                        {stack.chart.partners === 0 ? 'N/A' : stack.chart.partners}
                      </Text>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
            <Box w="100%" h="1px" bg={trioUITheme.colors['neutral-30']} my="16px" />
            <Box h="46px" w="100%" display="flex" justifyContent="flex-start" alignItems="center">
              <Box
                w="80px"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                mr="10px"
              >
                <Box mb="16px" w="16px" h="16px" bg={trioUITheme.colors['primary-70']} />
                <Text fontSize="12px" color={trioUITheme.colors['neutral-90']}>
                  by Trio
                </Text>
              </Box>
              <Box
                w="80px"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
              >
                <Box mb="16px" w="16px" h="16px" bg={trioUITheme.colors['secondary-60']} />
                <Text fontSize="12px" color={trioUITheme.colors['neutral-90']}>
                  by Partners
                </Text>
              </Box>
            </Box>
          </>
        ) : (
          <ChartEmptyState
            title="Your search didn’t match any result"
            subTitle="Try to choose different options."
          />
        )}
      </Box>
      {isLoading && (
        <Box height="100%" display="flex" justifyContent="center" alignItems="center">
          <Loading size={25} />
        </Box>
      )}
    </Box>
  );
};

export default ChartAvgTimeByTechStack;
